import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { ymd } from '@/services/appointments.service';
import { nearestQuarterHour, tablesService, type DiningTable, type SuggestedTable } from '@/services/tables.service';

interface Props {
  open: boolean;
  onClose: () => void;
  tables: DiningTable[];
  hasReservationService: boolean;
  onSeated: () => void;
}

export function WalkInModal({ open, onClose, tables, hasReservationService, onSeated }: Props) {
  const [partySize, setPartySize] = useState('2');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selected, setSelected] = useState<string>('');
  const [suggestion, setSuggestion] = useState<SuggestedTable | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPartySize('2'); setCustomerName(''); setCustomerPhone(''); setSelected(''); setSuggestion(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const n = parseInt(partySize, 10);
    if (!Number.isFinite(n) || n < 1) return;
    tablesService.suggest({ partySize: n, date: ymd(new Date()), time: nearestQuarterHour() })
      .then((r) => { setSuggestion(r.table); if (r.table && !selected) setSelected(r.table.tableUid); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partySize]);

  const partyN = parseInt(partySize, 10) || 0;
  const chosen = tables.find((t) => t.tableUid === selected);
  const tooSmall = !!chosen && chosen.seats < partyN;

  const submit = async () => {
    if (!selected) { toast.error('Pick a table first.'); return; }
    if (!hasReservationService) { toast.error('Add a reservation service first (Appointments → Services).'); return; }
    setBusy(true);
    try {
      await tablesService.walkIn({
        tableUid: selected, partySize: partyN,
        customerName: customerName.trim() || undefined,
        customerPhone: customerPhone.trim() || undefined,
        force: tooSmall,
      });
      toast.success('Walk-in seated');
      onSeated();
      onClose();
    } catch (e: any) {
      if (!e?.response) toast.error('Could not seat the walk-in.');
    } finally { setBusy(false); }
  };

  const active = tables.filter((t) => t.isActive);

  return (
    <Modal open={open} onClose={onClose} title="Seat a walk-in" widthClass="max-w-md">
      {!hasReservationService && (
        <p className="mb-4 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning">
          No reservation service is set up yet — add one under Appointments → Services before seating walk-ins.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Party size" required>
          <Input type="number" min={1} max={50} value={partySize} onChange={(e) => setPartySize(e.target.value)} />
        </FormField>
        <FormField label="Name" hint="Optional">
          <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Guest name" />
        </FormField>
      </div>
      <FormField label="Phone" hint="Optional">
        <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+971 5xx xxx xxx" />
      </FormField>

      <div className="mt-1 mb-1.5 flex items-center justify-between text-[13px] font-medium text-text-secondary">
        <span>Table</span>
        {suggestion && (
          <span className="inline-flex items-center gap-1 text-2xs font-medium text-primary">
            <Sparkles size={11} /> Suggested: {suggestion.name}
          </span>
        )}
      </div>
      <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
        {active.map((t) => {
          const small = t.seats < partyN;
          return (
            <button
              key={t.tableUid}
              type="button"
              onClick={() => setSelected(t.tableUid)}
              className={clsx(
                'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                selected === t.tableUid ? 'border-primary bg-primary-soft' : 'border-border hover:border-border-strong',
              )}
            >
              <span className="flex items-center gap-2 font-medium text-text-primary">
                {t.name}
                {suggestion?.tableUid === t.tableUid && <Sparkles size={12} className="text-primary" />}
              </span>
              <span className={clsx('text-xs', small ? 'text-warning' : 'text-text-muted')}>{t.seats} seats{t.area ? ` · ${t.area}` : ''}</span>
            </button>
          );
        })}
        {active.length === 0 && <p className="py-6 text-center text-sm text-text-muted">No active tables yet.</p>}
      </div>
      {tooSmall && <p className="mt-2 text-xs text-warning">{chosen!.name} seats {chosen!.seats}, fewer than the party of {partyN}.</p>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!selected || partyN < 1} onClick={submit}>Seat now</Button>
      </div>
    </Modal>
  );
}
